import { getScopedDomain } from '../lib/tenantScope';
// Voicemails (AI) — lists rows from `stratus_ext.voicemail_metadata` with the
// transcription / summary / sentiment that the voicemail_ai_worker fills in.
// Sits alongside the legacy /voicemail page.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCheck, Trash2, X, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

export default function VoicemailAI() {
  const me = api.me();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<any | null>(null);
  const [filter, setFilter] = useState<{ heard?: string; archived?: string }>({ archived: 'false' });

  const { data: list = [] } = useQuery({
    queryKey: ['vm-meta', getScopedDomain(me?.domain_uuid), filter],
    queryFn: () => getScopedDomain(me?.domain_uuid)
      ? api.voicemailMetadata.list({ domain_uuid: getScopedDomain(me?.domain_uuid), ...filter, limit: 100 })
      : Promise.resolve([]),
    enabled: !!getScopedDomain(me?.domain_uuid),
  });

  const markHeard = useMutation({
    mutationFn: (id: string) => api.voicemailMetadata.markHeard(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vm-meta'] }),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.voicemailMetadata.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vm-meta'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.voicemailMetadata.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vm-meta'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Sparkles size={20} /> Voicemails (AI)</h1>
        <span className="text-xs text-textSecondary">transcription · summary · sentiment</span>
      </div>

      <div className="card p-3 flex items-center gap-2">
        <select className="input" value={filter.heard ?? ''} onChange={e => setFilter({ ...filter, heard: e.target.value || undefined })}>
          <option value="">All</option>
          <option value="false">Unheard</option>
          <option value="true">Heard</option>
        </select>
        <select className="input" value={filter.archived ?? ''} onChange={e => setFilter({ ...filter, archived: e.target.value || undefined })}>
          <option value="">Active + archived</option>
          <option value="false">Active only</option>
          <option value="true">Archived only</option>
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr>
            <th></th>
            <th>From</th>
            <th>To (ext)</th>
            <th>When</th>
            <th>Duration</th>
            <th>Sentiment</th>
            <th>Summary</th>
            <th></th>
          </tr></thead>
          <tbody>
            {list.map((v: any) => (
              <tr key={v.vm_uuid} className={`hover:bg-elevated cursor-pointer ${!v.heard ? 'font-medium' : ''}`} onClick={() => setPicked(v)}>
                <td>
                  {!v.heard && <span className="inline-block w-2 h-2 rounded-full bg-primary" />}
                  {v.pv_archived && <Archive size={12} className="inline text-textSecondary ml-1" />}
                </td>
                <td className="font-mono">{v.caller || '—'}{v.caller_name ? <div className="text-xs text-textSecondary">{v.caller_name}</div> : null}</td>
                <td className="font-mono">{v.callee}</td>
                <td className="text-xs">{new Date(v.created_time).toLocaleString()}</td>
                <td>{v.duration_seconds ? `${v.duration_seconds}s` : '—'}</td>
                <td><SentimentChip score={v.sentiment_score} /></td>
                <td className="text-xs max-w-md truncate">{v.summary || (v.transcription ? '(transcribed)' : <span className="text-textSecondary">pending…</span>)}</td>
                <td onClick={e => e.stopPropagation()} className="space-x-1 whitespace-nowrap">
                  {!v.heard && <button className="btn-ghost p-1" title="Mark heard" onClick={() => markHeard.mutate(v.vm_uuid)}><CheckCheck size={14} /></button>}
                  {!v.pv_archived && <button className="btn-ghost p-1" title="Archive" onClick={() => archive.mutate(v.vm_uuid)}><Archive size={14} /></button>}
                  <button className="btn-ghost p-1 text-danger" title="Delete" onClick={() => confirm('Delete?') && remove.mutate(v.vm_uuid)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-textSecondary">No voicemails matching filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {picked && <Detail vm={picked} onClose={() => setPicked(null)} />}
    </div>
  );
}

function SentimentChip({ score }: { score: number | null }) {
  if (score == null) return <span className="text-textSecondary">—</span>;
  const cls = score > 25 ? 'bg-success/20 text-success'
            : score < -25 ? 'bg-danger/20 text-danger'
            : 'bg-warning/20 text-warning';
  const label = score > 25 ? 'positive' : score < -25 ? 'negative' : 'neutral';
  return <span className={`badge ${cls}`}>{label} ({score})</span>;
}

function Detail({ vm, onClose }: { vm: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Voicemail · {vm.callee}</h2>
          <button className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-textSecondary">From:</span> <span className="font-mono">{vm.caller || '—'}</span></div>
          <div><span className="text-textSecondary">When:</span> {new Date(vm.created_time).toLocaleString()}</div>
          <div><span className="text-textSecondary">Duration:</span> {vm.duration_seconds || '—'}s</div>
          <div><span className="text-textSecondary">Sentiment:</span> <SentimentChip score={vm.sentiment_score} /></div>
        </div>
        {vm.wav_file && (
          <audio controls className="w-full" src={`/api/voicemail/audio/${encodeURIComponent(vm.wav_file)}`} />
        )}
        {vm.summary && (
          <div>
            <h3 className="text-sm font-semibold mb-1">Summary</h3>
            <p className="text-sm bg-elevated p-3 rounded">{vm.summary}</p>
          </div>
        )}
        {vm.transcription && (
          <div>
            <h3 className="text-sm font-semibold mb-1">Transcription</h3>
            <p className="text-sm bg-elevated p-3 rounded whitespace-pre-wrap font-mono">{vm.transcription}</p>
          </div>
        )}
        {!vm.transcription && (
          <div className="text-sm text-textSecondary p-3 bg-elevated rounded">
            Transcription pending — the AI worker polls every 60s. Attempts: {vm.attempts ?? 0}.
            {vm.last_error && <div className="text-danger mt-1">Last error: {vm.last_error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
